import React, { Suspense, useEffect, useState, useRef, lazy } from 'react'
import { Switch, withRouter } from 'react-router-dom'
import { SemanticToastContainer } from 'react-semantic-toasts'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import '@store/i18n'
import { routes } from '@store/routes/index'
import { normalizeUiLanguage } from '@services/helpers/languages'
import 'react-semantic-toasts/styles/react-semantic-alert.css'
import '@styles/default.css'
import '@styles/themes.css'
import GlobalStyle from '@styles/globalStyles'
import { ensureSemanticUiStyles } from '@styles/ensureSemanticUiStyles'
import PrivateRoute from '@components/routes/PrivateRoute'
import PublicRoute from '@components/routes/PublicRoute'
import SpinnerSegment from '@components/SpinnerSegment'
import { dismissBootSplash } from '@components/general/BootSplash'
import Error from '@public/Error'

const Home = lazy(() => import('@public/Home'))
const Login = lazy(() => import('@public/auth/Login'))
const Activation = lazy(() => import('@public/auth/Activation'))
const ResetPassword = lazy(() => import('@public/auth/ResetPassword'))
const ConfirmResetPassword = lazy(() => import('@public/auth/ConfirmResetPassword'))
const GDPRView = lazy(() => import('@public/gdprs/GDPRView'))
const GDPRAgreementView = lazy(() => import('@public/gdprs/GDPRAgreementView'))
const ContractEsignView = lazy(() => import('@public/contracts/ContractEsignView'))
const KycStartView = lazy(() => import('@public/verification/KycStartView'))
const KycCompleteView = lazy(() => import('@public/verification/KycCompleteView'))
const PublicApplyForm = lazy(() => import('@dashboard/hr/jobs/views/PublicApplyForm'))
const Dashboard = lazy(() => import('@dashboard/Dashboard'))
const AttendanceTracker = lazy(() => import('@dashboard/trackers/attendance'))
const WarehouseTracker = lazy(() => import('@dashboard/trackers/warehousing'))

const App = () => {
    const { i18n, t } = useTranslation()
    const language = useSelector((state) => state.language)
    const theme = useSelector((state) => state.global_pref)
    const mode = useSelector((state) => state?.theme || 'light')
    const [bootVisible, setBootVisible] = useState(true)
    const mountedAtRef = useRef(Date.now())

    useEffect(() => {
        const normalized = normalizeUiLanguage(language, 'en')
        if (normalizeUiLanguage(i18n.language, '') !== normalized) {
            i18n.changeLanguage(normalized)
        }

        document.body.classList = ''
        document.body.classList.add(mode)

        ensureSemanticUiStyles().catch(() => {})

        const elapsed = Date.now() - mountedAtRef.current
        const wait = Math.max(0, 250 - elapsed)
        let cancelled = false

        const hideTimer = setTimeout(() => {
            dismissBootSplash().then(() => {
                if (!cancelled) setBootVisible(false)
            })
        }, wait)

        return () => {
            cancelled = true
            clearTimeout(hideTimer)
        }
        // eslint-disable-next-line
    }, [])

    if (bootVisible) {
        return null
    }

    return (
        <Suspense fallback={<SpinnerSegment loading={true} loadingMessage={t('loading_resources')} fullHeight />}>
            <GlobalStyle theme={theme} />
            <Switch>
                <PrivateRoute path="/dashboard/" component={Dashboard} isFullPage={true} />
                <PrivateRoute path="/warehousing/" component={WarehouseTracker} isFullPage={true} />
                <PrivateRoute path="/attendance/" component={AttendanceTracker} isFullPage={true} />
                <PublicRoute exact restricted={false} path="/" component={Home} isFullPage={true} />
                <PublicRoute
                    exact
                    restricted={false}
                    path={routes.GDPRS + 'agreement/:uuid'}
                    component={GDPRAgreementView}
                    isFullPage={true}
                />
                <PublicRoute
                    exact
                    restricted={false}
                    path={routes.GDPRS + ':uuid'}
                    component={GDPRView}
                    isFullPage={true}
                />
                <PublicRoute
                    exact
                    restricted={false}
                    path="/contracts/esign/:uuid"
                    component={ContractEsignView}
                    isFullPage={true}
                />
                <PublicRoute
                    exact
                    restricted={false}
                    path="/verification/kyc"
                    component={KycStartView}
                    isFullPage={true}
                />
                <PublicRoute
                    exact
                    restricted={false}
                    path="/verification/kyc/complete"
                    component={KycCompleteView}
                    isFullPage={true}
                />
                <PublicRoute exact restricted={true} path="/login/" component={Login} isFullPage={true} />
                <PublicRoute
                    exact
                    restricted={false}
                    path={routes.PUBLIC_APPLICATION_FORM + ':id'}
                    component={PublicApplyForm}
                    isFullPage={true}
                />
                <PublicRoute
                    exact
                    restricted={false}
                    path={routes.PUBLIC_FORM_SHARE + ':uuid'}
                    component={PublicApplyForm}
                    isFullPage={true}
                />
                <PublicRoute
                    exact
                    restricted={false}
                    path="/activate/:uid/:token"
                    component={Activation}
                    isFullPage={true}
                />
                <PublicRoute
                    exact
                    restricted={false}
                    path="/password-reset"
                    component={ResetPassword}
                    isFullPage={true}
                />
                <PublicRoute
                    exact
                    restricted={false}
                    path="/password/reset/confirm/:uid/:token"
                    component={ConfirmResetPassword}
                    isFullPage={true}
                />
                <PublicRoute
                    restricted={false}
                    exact
                    path="*"
                    component={() => <Error code="404" message="Page not found, Sorry!" />}
                    isFullPage={true}
                />
            </Switch>
            <SemanticToastContainer />
        </Suspense>
    )
}

export default withRouter(App)
